import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import axios from 'axios';
import { getUserRole, isMenuVisible, getSectionPerms } from '../../permissions';
import { 
  LayoutDashboard, 
  Database, 
  Menu, 
  X, 
  LogOut, 
  ChevronRight,
  ChevronDown,
  User,
  Building2,
  GitBranch,
  Settings,
  Tag,
  Package,
  FolderTree,
  Cpu,
  LayoutGrid,
  Percent,
  Ruler,
  PackagePlus,
  Warehouse,
  Users,
  UserCheck,
  FileText,
  ShoppingCart,
  Receipt,
  CreditCard,
  ClipboardList,
  RotateCcw,
  BadgeDollarSign,
  FileCheck,
  FileBadge,
  Banknote,
  Truck,
  ReceiptText,
  ArrowLeftRight,
  Layers,
  Wallet,
  Scissors,
  Headphones,
  UsersRound,
  ShieldCheck,
  ArrowRightLeft,
  Search,
  CalendarDays,
  CalendarCheck,
  ListChecks
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_source-scout-4/artifacts/hca8zcbd_WhatsApp%20Image%202026-02-28%20at%2022.18.51.jpeg";

const orgDataSubItems = [
  { id: 'users', label: 'Users', icon: UsersRound, path: '/admin/org-data/users' },
  { id: 'roles', label: 'Roles', icon: ShieldCheck, path: '/admin/org-data/roles' },
];

const hrSubItems = [
  { id: 'attendance', label: 'Attendance', icon: CalendarCheck, path: '/admin/hr/attendance' },
  { id: 'leaves', label: 'Leaves', icon: CalendarDays, path: '/admin/hr/leaves' },
];

const masterDataSubItems = [
  { id: 'companies', label: 'Companies', icon: Building2, path: '/admin/master-data/companies' },
  { id: 'branches', label: 'Branches', icon: GitBranch, path: '/admin/master-data/branches' },
  { id: 'vendors', label: 'Vendors', icon: Users, path: '/admin/master-data/vendors' },
  { id: 'customers', label: 'Customers', icon: UserCheck, path: '/admin/master-data/customers' },
  { id: 'products', label: 'Products', icon: Package, path: '/admin/master-data/products' },
  { id: 'categories', label: 'Categories', icon: FolderTree, path: '/admin/master-data/categories' },
  { id: 'sub-categories', label: 'Sub Categories', icon: Layers, path: '/admin/master-data/sub-categories' },
  { id: 'warranty', label: 'Warranty', icon: ShieldCheck, path: '/admin/master-data/warranty' },
  { id: 'racks', label: 'Racks', icon: LayoutGrid, path: '/admin/master-data/racks' },
  { id: 'taxes', label: 'Taxes', icon: Percent, path: '/admin/master-data/taxes' },
  { id: 'units', label: 'Units', icon: Ruler, path: '/admin/master-data/units' },
  { id: 'payment-terms', label: 'Payment Terms', icon: FileText, path: '/admin/master-data/payment-terms' },
  { id: 'payment-modes', label: 'Payment Modes', icon: Wallet, path: '/admin/master-data/payment-modes' },
];

const purchaseSubItems = [
  { id: 'purchase-bills', label: 'Purchase Bills', icon: Receipt, path: '/admin/purchase/bills' },
  { id: 'inward-challan', label: 'Inward Delivery Challan', icon: Truck, path: '/admin/purchase/inward-challan' },
  { id: 'return-inward-challan', label: 'Return Inward DC', icon: RotateCcw, path: '/admin/purchase/return-inward-challan' },
  { id: 'payment-out', label: 'Payment Out', icon: CreditCard, path: '/admin/purchase/payment-out' },
  { id: 'purchase-orders', label: 'Purchase Orders', icon: ClipboardList, path: '/admin/purchase/orders' },
  { id: 'purchase-return', label: 'Purchase Return / Debit Notes', icon: RotateCcw, path: '/admin/purchase/return' },
];

const saleSubItems = [
  { id: 'sale-invoices', label: 'Sale Invoices', icon: ReceiptText, path: '/admin/sale/invoices' },
  { id: 'estimates', label: 'Estimates / Quotations', icon: FileCheck, path: '/admin/sale/estimates' },
  { id: 'proforma-invoice', label: 'Proforma Invoice', icon: FileBadge, path: '/admin/sale/proforma' },
  { id: 'payment-in', label: 'Payment In', icon: BadgeDollarSign, path: '/admin/sale/payment-in' },
  { id: 'delivery-challan', label: 'Delivery Challan', icon: Truck, path: '/admin/sale/delivery-challan' },
  { id: 'sale-return', label: 'Sale Return / Credit Note', icon: RotateCcw, path: '/admin/sale/return' },
];

const servicesSubItems = [
  { id: 'leads', label: 'Leads', icon: FileText, path: '/admin/services/leads' },
  { id: 'leads-data', label: 'Leads Data', icon: UsersRound, path: '/admin/services/leads-data' },
  { id: 'tickets', label: 'Tickets', icon: ClipboardList, path: '/admin/services/tickets' },
  { id: 'receivables', label: 'Receivables', icon: Banknote, path: '/admin/services/receivables' },
  { id: 'lead-sources', label: 'Lead Source', icon: FileText, path: '/admin/services/lead-sources' },
  { id: 'requirements', label: 'Requirements', icon: ClipboardList, path: '/admin/services/requirements' },
  { id: 'service-steps', label: 'Steps', icon: ListChecks, path: '/admin/services/step-master' },
  { id: 'service-tasks', label: 'Tasks', icon: ClipboardList, path: '/admin/services/task-master' },
];

const settingsSubItems = [
  { id: 'invoice-settings', label: 'Invoice Settings', icon: ReceiptText, path: '/admin/settings/invoice-settings' },
  { id: 'data-management', label: 'Config Settings', icon: Database, path: '/admin/settings/config' },
];

const menuItems = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    path: '/admin',
  },
  {
    id: 'search-item',
    label: 'Search Item',
    icon: Search,
    path: '/admin/search-item',
  },
  {
    id: 'stock-availability',
    label: 'Stock Availability',
    icon: Tag,
    path: '/admin/stock-availability',
  },
  {
    id: 'org-data',
    label: 'Org Data',
    icon: UsersRound,
    path: '/admin/org-data',
    subItems: orgDataSubItems,
  },
  {
    id: 'hr',
    label: 'HR',
    icon: Users,
    path: '/admin/hr',
    subItems: hrSubItems,
  },
  {
    id: 'master-data',
    label: 'Master Data',
    icon: Database,
    path: '/admin/master-data',
    subItems: masterDataSubItems,
  },
  {
    id: 'config-settings',
    label: 'Settings',
    icon: Settings,
    path: '/admin/settings',
    subItems: settingsSubItems,
  },
  {
    id: 'inward',
    label: 'Inward',
    icon: PackagePlus,
    path: '/admin/inward',
  },
  {
    id: 'inventory',
    label: 'Inventory',
    icon: Warehouse,
    path: '/admin/inventory',
  },
  {
    id: 'internal-transfer',
    label: 'Internal Transfer',
    icon: ArrowRightLeft,
    path: '/admin/inventory/internal-transfers',
  },
  {
    id: 'part-sale-tracker',
    label: 'Part Sale Tracker',
    icon: Scissors,
    path: '/admin/part-sale-tracker',
  },
  {
    id: 'purchase',
    label: 'Purchase & Expenses',
    icon: ShoppingCart,
    path: '/admin/purchase',
    subItems: purchaseSubItems,
  },
  {
    id: 'sale',
    label: 'Sale',
    icon: BadgeDollarSign,
    path: '/admin/sale',
    subItems: saleSubItems,
  },
  {
    id: 'transactions',
    label: 'Transactions',
    icon: ArrowLeftRight,
    path: '/admin/transactions',
  },
  {
    id: 'services',
    label: 'Services Hub',
    icon: Headphones,
    path: '/admin/services',
    subItems: servicesSubItems,
  },
];

export const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [openMenus, setOpenMenus] = useState({});

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      navigate('/admin/login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    if (parsedUser.must_change_password) {
      navigate('/admin/change-password', { replace: true });
      return;
    }

    setUser(parsedUser);

    // Global 401 interceptor — auto-logout on expired session
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/admin/login';
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [navigate]);

  const handleLogout = async () => {
    const token = localStorage.getItem('token');
    try {
      if (token) {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch {
      // A network problem must not trap the user in the current session.
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/admin/login');
    }
  };

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Among all leaf (no subItems) menu paths, returns the longest one matching the current path.
  // Prevents both /admin/inventory and /admin/inventory/internal-transfers from highlighting simultaneously.
  const getActiveLeafPath = () => {
    const candidates = menuItems.filter(i => !i.subItems && i.path !== '/admin').map(i => i.path);
    let best = '';
    for (const p of candidates) {
      if (location.pathname === p || location.pathname.startsWith(p + '/')) {
        if (p.length > best.length) best = p;
      }
    }
    return best;
  };
  const activeLeafPath = getActiveLeafPath();

  const userRole = user?.role || getUserRole();

  const filteredMenuItems = useMemo(() => {
    return menuItems
      .filter(item => isMenuVisible(item.id, userRole))
      .map(item => {
        if (!item.subItems) return item;
        const sPerms = getSectionPerms(item.id, userRole);
        let subItems = item.subItems;
        if (sPerms.hiddenSubItems?.length) {
          subItems = subItems.filter(si => !sPerms.hiddenSubItems.includes(si.id));
        }
        return { ...item, subItems };
      })
      .filter(item => {
        // A group whose original definition had sub-items but all are now hidden
        // must not render as a leaf nav button (would navigate to an undefined route).
        const original = menuItems.find(m => m.id === item.id);
        if (original?.subItems?.length > 0) {
          return item.subItems.length > 0;
        }
        return true;
      });
  }, [userRole]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        data-testid="admin-sidebar"
        className={`fixed top-0 left-0 h-full bg-slate-900 z-50 transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-20'
        } ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        {/* Sidebar Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
          {sidebarOpen && (
            <img
              src={LOGO_URL}
              alt="Tejas Info Solutions"
              className="h-10 w-auto object-contain"
            />
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="sidebar-toggle"
            className="hidden lg:flex p-2 text-slate-400 hover:text-white transition-colors"
          >
            <ChevronRight className={`h-5 w-5 transition-transform ${sidebarOpen ? 'rotate-180' : ''}`} />
          </button>
          <button
            onClick={() => setMobileSidebarOpen(false)}
            className="lg:hidden p-2 text-slate-400 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Menu Items */}
        <nav className="p-4 space-y-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 140px)' }}>
          {filteredMenuItems.map((item) => {
            const Icon = item.icon;
            const hasSubItems = item.subItems && item.subItems.length > 0;
            const active = hasSubItems ? isActive(item.path) : (item.path === '/admin' ? location.pathname === '/admin' : item.path === activeLeafPath);
            
            if (hasSubItems) {
              const isOpen = openMenus[item.id] || false;
              const isActiveSection = location.pathname.startsWith(item.path);
              return (
                <div key={item.id}>
                  <button
                    onClick={() => setOpenMenus(prev => ({ ...prev, [item.id]: !prev[item.id] }))}
                    data-testid={`menu-${item.id}`}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-sm transition-all duration-200 ${
                      isActiveSection
                        ? 'bg-slate-800 text-white'
                        : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="h-5 w-5 flex-shrink-0" />
                      {sidebarOpen && <span className="font-medium">{item.label}</span>}
                    </div>
                    {sidebarOpen && (
                      <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    )}
                  </button>
                  
                  {sidebarOpen && isOpen && (
                    <div className="ml-4 mt-1 space-y-1 border-l border-slate-700 pl-4">
                      {item.subItems.map((subItem) => {
                        const SubIcon = subItem.icon;
                        const subActive = location.pathname === subItem.path;
                        
                        return (
                          <button
                            key={subItem.id}
                            onClick={() => {
                              navigate(subItem.path);
                              setMobileSidebarOpen(false);
                            }}
                            data-testid={`menu-${subItem.id}`}
                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-all duration-200 ${
                              subActive
                                ? 'bg-orange-500 text-white'
                                : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                            }`}
                          >
                            <SubIcon className="h-4 w-4 flex-shrink-0" />
                            <span>{subItem.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            
            return (
              <button
                key={item.id}
                onClick={() => {
                  navigate(item.path);
                  setMobileSidebarOpen(false);
                }}
                data-testid={`menu-${item.id}`}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all duration-200 ${
                  active
                    ? 'bg-orange-500 text-white'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {sidebarOpen && (
                  <span className="font-medium">{item.label}</span>
                )}
              </button>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          {sidebarOpen ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="text-white font-medium text-sm">{user.name}</div>
                  <div className="text-slate-500 text-xs">{user.role}</div>
                </div>
              </div>
              <button
                onClick={handleLogout}
                data-testid="logout-btn"
                className="p-2 text-slate-400 hover:text-red-400 transition-colors"
                title="Logout"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          ) : (
            <button
              onClick={handleLogout}
              data-testid="logout-btn-collapsed"
              className="w-full flex justify-center p-2 text-slate-400 hover:text-red-400 transition-colors"
              title="Logout"
            >
              <LogOut className="h-5 w-5" />
            </button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}`}>
        {/* Top Bar */}
        <header 
          data-testid="admin-header"
          className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-8 sticky top-0 z-30"
        >
          <button
            onClick={() => setMobileSidebarOpen(true)}
            data-testid="mobile-menu-btn"
            className="lg:hidden p-2 text-slate-600 hover:text-slate-900"
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex-1 lg:hidden" />

          <div className="text-right">
            <div className="text-sm font-medium text-slate-900">{user.name}</div>
            <div className="text-xs text-slate-500">{user.email}</div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
